//@flow
import * as Immutable from 'immutable';

import { Limits } from 'common/models/flow/Limits';
import {
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import type { LimitsModelType } from 'common/models/flow/Limits';
import { RiderResourceLimit } from './RiderResourceLimit';
import { VolumeLimit } from './VolumeLimit';
import DataUtils from 'common/utils/DataUtils';
import { Units } from 'common/enums/UnitEnum';
import I18nHelper from 'common/helpers/I18nHelper';

const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
const units = {
    volume: {
        preferredUnits: [Units.list.volume.CubicMeter],
        sourceUnit: Units.list.volume.CubicMeter
    },
    distance: {
        preferredUnits: [Units.list.length.Kilometer, Units.list.length.Mile],
        sourceUnit: Units.list.length.Kilometer
    },
    weight: {
        preferredUnits: [Units.list.mass.Kilogram, Units.list.mass.Pound],
        sourceUnit: Units.list.mass.Kilogram
    },
    travelTime: {
        preferredUnits: [Units.list.time.Minute],
        sourceUnit: Units.list.time.Minute
    }
};
@validatable
class InternalLimits extends Limits {
    static fromJS(json: LimitsModelType): InternalLimits {
        const state: Object = Object.assign({}, json);
        state.resourceLimits = state.resourceLimits
            ? Immutable.List(state.resourceLimits).map(item =>
                  RiderResourceLimit.fromJS(item)
              )
            : state.resourceLimits;
        state.volumeLimit = state.volumeLimit
            ? VolumeLimit.fromJS(state.volumeLimit)
            : state.volumeLimit;
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { resourceLimits, volumeLimit } = this;
        if (resourceLimits) {
            return resourceLimits.some(resourceLimit =>
                resourceLimit.hasValidationErrors()
            );
        }
        if (volumeLimit) {
            return volumeLimit.hasValidationErrors();
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { resourceLimits, volumeLimit } = this;

        if (resourceLimits) {
            resourceLimits = resourceLimits.map(resourceLimit =>
                resourceLimit.setAllValidationErrors()
            );
        }
        if (volumeLimit) {
            volumeLimit = volumeLimit.setAllValidationErrors();
        }
        return this.setResourceLimits(resourceLimits).setVolumeLimit(
            volumeLimit
        );
    }

    sanitize(): this {
        const { resourceLimits } = this;
        if (resourceLimits) {
            return this.setResourceLimits(
                resourceLimits.filter(resourceLimit => {
                    const { lowerThresh, higherThresh } = resourceLimit;
                    return (
                        !DataUtils.isEmpty(lowerThresh) ||
                        !DataUtils.isEmpty(higherThresh)
                    );
                })
            );
        }
        return this;
    }

    get resourceLimits(): ?Immutable.List<RiderResourceLimit> {
        return this._state.get('resourceLimits');
    }

    get volumeLimit(): ?VolumeLimit {
        return this._state.get('volumeLimit');
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        let sanitizedLimits = this.sanitize();
        sanitizedLimits = super.defaultSanitize(sanitizedLimits);
        if (sanitizedLimits) {
            const sanitizedVolumeLimit = sanitizedLimits.volumeLimit?.defaultSanitize();
            const sanitizedResourceLimits = sanitizedLimits.resourceLimits.map(
                resourceLimit => resourceLimit?.defaultSanitize()
            );

            return sanitizedLimits
                .setResourceLimits(sanitizedResourceLimits)
                .setVolumeLimit(sanitizedVolumeLimit);
        }
        return sanitizedLimits;
    }

    static defaultInit({ props }): InternalLimits {
        const volumeLimit = VolumeLimit.defaultInit({ props });
        const resourceLimits = Immutable.List([
            RiderResourceLimit.defaultInit({ props })
        ]);
        return InternalLimits.fromJS({})
            .setResourceLimits(resourceLimits)
            .setVolumeLimit(volumeLimit);
    }

    getPreferredUnitText(unitConverter: Object, type: string) {
        switch (type) {
            case 'task':
                return nsTranslate('count');
            default:
                return unitConverter.getConvertedUnitDisplayText({
                    preferredUnits: units[type]?.preferredUnits,
                    sourceUnit: units[type]?.sourceUnit,
                    pluralizeUnit: false
                });
        }
    }
}

export { InternalLimits as Limits };
